import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Upload, FileSpreadsheet, Info, CheckCircle, Loader2, Download,
  ChevronDown, ChevronRight, PackageCheck, Boxes, Warehouse, RotateCcw,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import apiClient from '@/services/apiClient';

const SeasonalPickSlipUpload = () => {
  const navigate = useNavigate();
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [preview, setPreview] = useState(null);
  const [confirmed, setConfirmed] = useState(null);
  const [recent, setRecent] = useState([]);
  const [expanded, setExpanded] = useState({});

  const loadRecent = useCallback(async () => {
    try {
      const r = await apiClient.get('/wms/seasonal/pick-slips', { params: { limit: 300 } });
      setRecent(r.data?.slips || []);
    } catch {
      // silent — recent list is best-effort
    }
  }, []);

  useEffect(() => { loadRecent(); }, [loadRecent]);

  const resetAll = () => {
    setSelectedFile(null);
    setPreview(null);
    setConfirmed(null);
    setExpanded({});
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const ext = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));
    if (!['.csv', '.xlsx', '.xls'].includes(ext)) {
      toast.error('Please select a CSV or Excel file');
      return;
    }
    setSelectedFile(file);
    setPreview(null);
    setConfirmed(null);
    setExpanded({});
  };

  const handlePreview = async () => {
    if (!selectedFile) {
      toast.error('Please select a file');
      return;
    }
    setPreviewing(true);
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      // apiClient defaults to application/json; drop it so the browser adds the
      // multipart boundary.
      const r = await apiClient.post('/wms/seasonal/pick-slips/upload-preview', formData, {
        headers: { 'Content-Type': undefined },
      });
      setPreview(r.data);
      setConfirmed(null);
      setExpanded({});
      toast.success(`Previewed ${r.data.slips_generated} slip(s) — ${r.data.pool_qty_total} pcs from pool, ${r.data.fg_balance_total} pcs to pick`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Preview failed');
    } finally {
      setPreviewing(false);
    }
  };

  const handleConfirm = async () => {
    if (!selectedFile) return;
    setConfirming(true);
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      const r = await apiClient.post('/wms/seasonal/pick-slips/upload-confirm', formData, {
        headers: { 'Content-Type': undefined },
      });
      setConfirmed(r.data);
      setPreview(null);
      setExpanded({});
      toast.success(`Created ${r.data.slips_generated} slip(s) — ${r.data.pool_boxes_allocated} pool bundle(s) tagged, ${r.data.fg_slips_created} FG slip(s) queued`);
      loadRecent();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Confirm failed');
    } finally {
      setConfirming(false);
    }
  };

  const toggle = (id) => setExpanded((p) => ({ ...p, [id]: !p[id] }));

  const downloadCsv = () => {
    const data = confirmed || preview;
    if (!data?.slips?.length) return;
    const header = ['Pick Slip', 'Slug', 'Customer', 'Barcode', 'Design Name', 'Shade', 'Size', 'Total Qty', 'From Pool', 'FG Balance'];
    const rows = [header];
    data.slips.forEach((s) => {
      (s.items || []).forEach((it) => {
        rows.push([s.pick_slip_number, s.slug, s.customer || '', it.barcode, it.design_name || '', it.shade || '', it.size || '', it.qty, it.pool_qty ?? 0, it.fg_balance ?? it.qty]);
      });
    });
    const csv = rows.map((r) => r.map((c) => {
      const v = String(c ?? '');
      return /[",\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v;
    }).join(',')).join('\n');
    const url = window.URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `seasonal_pick_slips_${data.batch_id?.slice(0, 8) || 'batch'}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  };

  const stat = (label, value, color) => (
    <div className="bg-white rounded-lg border border-[#E5DFD0] p-4">
      <p className="text-xs text-[#5A5F6E]">{label}</p>
      <p className={`text-2xl font-bold mt-1 ${color || 'text-[#1A1F2E]'}`}>{value}</p>
    </div>
  );

  const activeData = confirmed || preview;

  const renderSlipDetail = (s) => {
    const poolItems = (s.items || []).filter((it) => (it.pool_qty || 0) > 0);
    const fgItems = (s.items || []).filter((it) => (it.fg_balance ?? it.qty) > 0);
    return (
      <div className="grid md:grid-cols-2 gap-4 py-2">
        {/* Common Pool */}
        <div className="rounded-lg border border-teal-200 bg-teal-50/50 p-3">
          <div className="flex items-center gap-2 text-teal-800 font-semibold text-sm mb-2">
            <Boxes className="w-4 h-4" /> Fulfilled from Common Pool (whole bundles)
          </div>
          {poolItems.length === 0 ? (
            <p className="text-xs text-teal-700/70">No pool bundles matched — this slip picks entirely from FG.</p>
          ) : (
            <table className="w-full text-xs">
              <thead>
                <tr className="text-left text-teal-700/80">
                  <th className="py-1 font-medium">Barcode</th>
                  <th className="py-1 font-medium">Design / Shade / Size</th>
                  <th className="py-1 font-medium text-right">Pool Qty</th>
                  <th className="py-1 font-medium">Bundles</th>
                </tr>
              </thead>
              <tbody>
                {poolItems.map((it, j) => (
                  <tr key={j} className="border-t border-teal-100 align-top">
                    <td className="py-1 font-mono">{it.barcode}</td>
                    <td className="py-1">{[it.design_name, it.shade, it.size].filter(Boolean).join(' / ') || '—'}</td>
                    <td className="py-1 text-right font-semibold text-teal-800">{it.pool_qty}</td>
                    <td className="py-1">
                      {(it.pool_boxes || []).map((b, k) => (
                        <span key={k} className="inline-block bg-teal-100 text-teal-800 rounded px-1.5 py-0.5 mr-1 mb-1 font-mono text-[10px]">
                          {b.box_number} · {b.qty}
                        </span>
                      ))}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* FG Balance */}
        <div className="rounded-lg border border-amber-200 bg-amber-50/50 p-3">
          <div className="flex items-center gap-2 text-amber-800 font-semibold text-sm mb-2">
            <Warehouse className="w-4 h-4" /> Balance to pick from FG (USNs)
          </div>
          {fgItems.length === 0 ? (
            <p className="text-xs text-amber-700/70">Fully covered by the Common Pool — nothing to pick from FG.</p>
          ) : (
            <table className="w-full text-xs">
              <thead>
                <tr className="text-left text-amber-700/80">
                  <th className="py-1 font-medium">Barcode</th>
                  <th className="py-1 font-medium">Design / Shade / Size</th>
                  <th className="py-1 font-medium text-right">FG Balance</th>
                </tr>
              </thead>
              <tbody>
                {fgItems.map((it, j) => (
                  <tr key={j} className="border-t border-amber-100">
                    <td className="py-1 font-mono">{it.barcode}</td>
                    <td className="py-1">{[it.design_name, it.shade, it.size].filter(Boolean).join(' / ') || '—'}</td>
                    <td className="py-1 text-right font-semibold text-amber-800">{it.fg_balance ?? it.qty}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-[#FFFBF5]" data-testid="seasonal-upload-page">
      <header className="bg-[#1A1F2E] text-[#F9FAFB] shadow-lg">
        <div className="max-w-6xl mx-auto px-4 md:px-6 h-16 flex items-center">
          <Button variant="ghost" onClick={() => navigate('/wms/fg')} className="text-[#F9FAFB] hover:bg-white/10 mr-3" data-testid="seasonal-back-btn">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <PackageCheck className="w-7 h-7 mr-3" />
          <div>
            <h1 className="text-xl font-bold font-display">Seasonal Pick Slip Upload</h1>
            <p className="text-sm text-[#E8DCC8]">Auto-match the Common Pool, then queue the FG balance</p>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 md:px-6 py-8 space-y-6">
        {/* Info */}
        <Card className="border-l-4 border-l-lime-500">
          <CardContent className="pt-4 flex gap-3">
            <Info className="w-5 h-5 text-lime-600 shrink-0 mt-0.5" />
            <div className="text-sm text-[#5A5F6E] space-y-1">
              <p><b>Required columns:</b> Slug, Barcode, D2 Allocated (CSV or Excel).</p>
              <p>Slips are generated <b>per slug</b> (≤300 pcs, a single barcode line is never split), numbered <b>SP-&lt;slug&gt;-NN</b>.</p>
              <p><b>Preview</b> first: each SKU line is matched against the <b>Common Pool</b> using <b>whole bundles only</b>; the remaining <b>FG balance</b> is queued as USN picking for FG pickers.</p>
              <p>Nothing is created or tagged until you press <b>Confirm &amp; Create</b>.</p>
            </div>
          </CardContent>
        </Card>

        {/* Upload */}
        {!confirmed && (
          <Card>
            <CardContent className="pt-5">
              <div className="flex flex-col sm:flex-row items-start sm:items-end gap-4">
                <div className="flex-1 w-full">
                  <label className="block text-xs font-medium text-[#5A5F6E] mb-1">Seasonal Pick Slip file</label>
                  <input
                    type="file"
                    accept=".csv,.xlsx,.xls"
                    onChange={handleFileChange}
                    className="block w-full text-sm text-[#1A1F2E] file:mr-3 file:py-2 file:px-4 file:rounded-md file:border-0 file:bg-lime-100 file:text-lime-700 file:font-semibold hover:file:bg-lime-200"
                    data-testid="seasonal-file-input"
                  />
                  {selectedFile && (
                    <p className="text-xs text-[#5A5F6E] mt-2 flex items-center gap-1">
                      <FileSpreadsheet className="w-3.5 h-3.5" /> {selectedFile.name}
                    </p>
                  )}
                </div>
                <Button
                  onClick={handlePreview}
                  disabled={!selectedFile || previewing}
                  className="bg-lime-600 hover:bg-lime-700 text-white"
                  data-testid="seasonal-preview-btn"
                >
                  {previewing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                  {previewing ? 'Previewing…' : 'Preview Allocation'}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Confirmed banner */}
        {confirmed && (
          <div className="space-y-4" data-testid="seasonal-created-banner">
            <Card className="border-l-4 border-l-emerald-500">
              <CardContent className="pt-4 flex items-center justify-between gap-4 flex-wrap">
                <div className="flex items-center gap-2 text-emerald-700">
                  <CheckCircle className="w-6 h-6" />
                  <div>
                    <p className="font-semibold">Pick slips created</p>
                    <p className="text-xs text-[#5A5F6E]">{confirmed.file_name}</p>
                  </div>
                </div>
                <Button onClick={resetAll} variant="outline" className="border-lime-500 text-lime-700 hover:bg-lime-50" data-testid="seasonal-reset-btn">
                  <RotateCcw className="w-4 h-4 mr-1.5" /> Upload another file
                </Button>
              </CardContent>
            </Card>
            <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
              {stat('Slugs', confirmed.slugs, 'text-[#1A1F2E]')}
              {stat('Slips Created', confirmed.slips_generated, 'text-lime-700')}
              {stat('Total Qty', confirmed.total_qty, 'text-[#1A1F2E]')}
              {stat('From Pool', confirmed.pool_qty_total, 'text-teal-700')}
              {stat('FG Balance', confirmed.fg_balance_total, 'text-amber-700')}
              {stat('Pool Bundles Tagged', confirmed.pool_boxes_allocated, 'text-teal-700')}
            </div>
          </div>
        )}

        {/* Preview / Result slips */}
        {activeData && (
          <div className="space-y-4" data-testid="seasonal-summary">
            {preview && !confirmed && (
              <>
                <div className="grid grid-cols-2 md:grid-cols-6 gap-3" data-testid="seasonal-preview-summary">
                  {stat('Rows Used', preview.rows_used, 'text-indigo-700')}
                  {stat('Slugs', preview.slugs, 'text-[#1A1F2E]')}
                  {stat('Slips', preview.slips_generated, 'text-lime-700')}
                  {stat('Total Qty', preview.total_qty, 'text-[#1A1F2E]')}
                  {stat('From Pool', preview.pool_qty_total, 'text-teal-700')}
                  {stat('FG Balance', preview.fg_balance_total, 'text-amber-700')}
                </div>
                <Card className="border-l-4 border-l-teal-500 bg-teal-50/40">
                  <CardContent className="pt-4 flex items-center justify-between gap-4 flex-wrap">
                    <p className="text-sm text-[#5A5F6E]">
                      <b className="text-teal-800">{preview.pool_boxes_matched}</b> whole pool bundle(s) will be tagged to these slips and{' '}
                      <b className="text-amber-800">{preview.fg_balance_total}</b> pcs will be queued for FG picking. Nothing is created yet.
                    </p>
                    <Button
                      onClick={handleConfirm}
                      disabled={confirming}
                      className="bg-emerald-600 hover:bg-emerald-700 text-white"
                      data-testid="seasonal-confirm-btn"
                    >
                      {confirming ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-2" />}
                      {confirming ? 'Creating…' : `Confirm & Create ${preview.slips_generated} slip(s)`}
                    </Button>
                  </CardContent>
                </Card>
              </>
            )}

            <div className="flex justify-end">
              <Button onClick={downloadCsv} variant="outline" className="border-emerald-500 text-emerald-700 hover:bg-emerald-50" data-testid="seasonal-download-csv-btn">
                <Download className="w-4 h-4 mr-1.5" /> Download CSV
              </Button>
            </div>

            <Card>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="seasonal-slips-table">
                    <thead>
                      <tr className="text-left text-[#5A5F6E] bg-gray-50 border-b">
                        <th className="px-3 py-2.5 font-medium w-8"></th>
                        <th className="px-3 py-2.5 font-medium">Pick Slip #</th>
                        <th className="px-3 py-2.5 font-medium">Slug</th>
                        <th className="px-3 py-2.5 font-medium">Customer</th>
                        <th className="px-3 py-2.5 font-medium text-right">Total</th>
                        <th className="px-3 py-2.5 font-medium text-right">From Pool</th>
                        <th className="px-3 py-2.5 font-medium text-right">FG Balance</th>
                      </tr>
                    </thead>
                    <tbody>
                      {activeData.slips.map((s, i) => (
                        <React.Fragment key={s.pick_slip_number}>
                          <tr className="border-b hover:bg-gray-50 cursor-pointer" onClick={() => toggle(s.pick_slip_number)} data-testid={`seasonal-slip-row-${i}`}>
                            <td className="px-3 py-2">{expanded[s.pick_slip_number] ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}</td>
                            <td className="px-3 py-2 font-mono font-semibold text-lime-700">{s.pick_slip_number}</td>
                            <td className="px-3 py-2 font-mono">{s.slug}</td>
                            <td className="px-3 py-2">{s.customer || <span className="text-gray-300">—</span>}</td>
                            <td className="px-3 py-2 text-right font-semibold">{s.total_qty}</td>
                            <td className="px-3 py-2 text-right">
                              {(s.pool_qty || 0) > 0
                                ? <Badge className="bg-teal-100 text-teal-700 border-0">{s.pool_qty}</Badge>
                                : <span className="text-gray-300">0</span>}
                            </td>
                            <td className="px-3 py-2 text-right">
                              {(s.fg_balance || 0) > 0
                                ? <Badge className="bg-amber-100 text-amber-700 border-0">{s.fg_balance}</Badge>
                                : <span className="text-gray-300">0</span>}
                            </td>
                          </tr>
                          {expanded[s.pick_slip_number] && (
                            <tr className="bg-gray-50/60">
                              <td></td>
                              <td colSpan={6} className="px-3 pb-3" data-testid={`seasonal-slip-detail-${i}`}>
                                {renderSlipDetail(s)}
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Recent */}
        {!activeData && (
          <Card>
            <CardContent className="pt-5">
              <h2 className="text-base font-semibold text-[#1A1F2E] mb-3">Recently Generated Slips</h2>
              {recent.length === 0 ? (
                <p className="text-sm text-[#5A5F6E]" data-testid="seasonal-empty">No seasonal pick slips generated yet.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="seasonal-recent-table">
                    <thead>
                      <tr className="text-left text-[#5A5F6E] bg-gray-50 border-b">
                        <th className="px-3 py-2 font-medium">Pick Slip #</th>
                        <th className="px-3 py-2 font-medium">Slug</th>
                        <th className="px-3 py-2 font-medium">Customer</th>
                        <th className="px-3 py-2 font-medium text-right">Lines</th>
                        <th className="px-3 py-2 font-medium text-right">Qty</th>
                        <th className="px-3 py-2 font-medium">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {recent.map((s, i) => (
                        <tr key={s.slip_id || i} className="border-b hover:bg-gray-50" data-testid={`seasonal-recent-row-${i}`}>
                          <td className="px-3 py-2 font-mono font-semibold text-lime-700">{s.pick_slip_number}</td>
                          <td className="px-3 py-2 font-mono">{s.slug}</td>
                          <td className="px-3 py-2">{s.customer || <span className="text-gray-300">—</span>}</td>
                          <td className="px-3 py-2 text-right">{s.line_count}</td>
                          <td className="px-3 py-2 text-right font-semibold">{s.total_qty}</td>
                          <td className="px-3 py-2"><Badge className="bg-lime-100 text-lime-700 border-0 text-[11px]">{s.status}</Badge></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
};

export default SeasonalPickSlipUpload;
